import React, { useEffect, useRef, useState } from "react";
import { handlePrintData } from "../../../utils/PrintData";
import { PrintIcon } from "../../../utils/TableActionIcons";
import ReportHeader from "../../../shared/ReportHeader";
import ReportFooter from "../../../shared/ReportFooter";
import { formatDate } from "../../../utils/FormateDateTime";
import { fetchData } from "../../../helpers/fetchData";
import CheckPermission from "../../../hooks/CheckPermission";

const isRenderableImageSource = (value) =>
  typeof value === "string" &&
  (value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:"));

const usePresignedImageSource = (objectKeyOrUrl) => {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let isMounted = true;

    const resolveSource = async () => {
      if (!objectKeyOrUrl) {
        setSrc("");
        return;
      }
      if (isRenderableImageSource(objectKeyOrUrl)) {
        setSrc(objectKeyOrUrl);
        return;
      }

      try {
        const response = await fetchData(
          "POST",
          "/v3/files/presigned-url?expiry=168",
          {
            objectKey: objectKeyOrUrl,
          },
        );

        if (isMounted) {
          setSrc(response?.presignedURL || "");
        }
      } catch (error) {
        if (isMounted) {
          setSrc("");
        }
      }
    };

    resolveSource();

    return () => {
      isMounted = false;
    };
  }, [objectKeyOrUrl]);

  return src;
};

const InlineCell = ({ label, value, className = "" }) => (
  <div
    className={`flex min-h-[25px] items-center gap-1 border border-gray-600 px-1.5 py-0.5 ${className}`}
  >
    <b className="whitespace-nowrap text-[12px]">{label}:</b>
    <span className="min-w-0 font-semibold leading-4 break-words">
      {value || ""}
    </span>
  </div>
);

const TextBox = ({ title, value, className = "min-h-[72px]" }) => (
  <div className="border border-gray-600">
    <div className="border-b border-gray-600 bg-gray-50 px-2 py-2 text-[14px] font-semibold leading-4">
      {title}:
    </div>
    <div
      className={`${className} whitespace-pre-wrap break-words px-2 py-1 text-[12px] font-semibold leading-4`}
    >
      {value || ""}
    </div>
  </div>
);

const CheckBox = ({ label, checked }) => (
  <span className="inline-flex items-center gap-1 font-semibold leading-4">
    <span className="inline-flex h-[15px] w-[15px] items-center justify-center border border-gray-600 text-[11px]">
      {checked ? "✓" : ""}
    </span>
    {label}
  </span>
);

function ViewExternalReferralFormReport({ selectedReferralData }) {
  const { hasPermission } = CheckPermission();
  const printRef = useRef(null);
  const data = selectedReferralData || {};
  const external = data?.external_referral || {};
  const vitals = data?.vitals || {};
  const patient = data?.patient || {};
  const practitioner = data?.practitioner || {};
  const signatureSrc = usePresignedImageSource(practitioner?.signature);
  const stampSrc = usePresignedImageSource(practitioner?.stamp);
  const gender = String(patient?.gender || "").toLowerCase();
  const referralPriority =
    external?.referral_priority || data?.type_of_referral;
  console.log(signatureSrc);
  return (
    <div
      ref={printRef}
      className="mx-auto flex min-h-screen max-w-[900px] flex-col bg-white text-[12px] text-gray-950"
    >
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 7mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .external-referral-print-root { max-width: none !important; font-size: 12px !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
      <div
        className="absolute top-8 right-20 cursor-pointer print:hidden"
        onClick={() =>
          handlePrintData(printRef?.current?.innerHTML, "ExternalReferralForm")
        }
      >
        <PrintIcon iconSize={20} />
      </div>

      <ReportHeader reportName="" />

      <div className="external-referral-print-root border border-gray-600">
        <div className="border-b border-gray-600 py-1 text-center text-[18px] font-semibold leading-tight">
          EXTERNAL REFERRAL FORM
        </div>

        <InlineCell label="Patient's Full Name" value={patient?.full_name} />
        <div className="grid grid-cols-[1fr_125px_145px_1fr]">
          <InlineCell
            label="MRN"
            value={patient?.file_no || patient?.mrn || patient?.patient_id}
          />
          <InlineCell label="Age" value={data?.age} />
          <div className="flex min-h-[25px] items-center gap-3 border border-gray-600 px-1.5 py-0.5">
            <b className="text-[12px]">Sex:</b>
            <CheckBox label="M" checked={gender === "male" || gender === "m"} />
            <CheckBox
              label="F"
              checked={gender === "female" || gender === "f"}
            />
          </div>

          <InlineCell label="Nationality" value={patient?.nationality} />
        </div>

        <InlineCell
          label="Allergic to"
          value={external?.allergic_to || "NONE"}
        />
        <div className="grid min-h-[29px] grid-cols-[1fr_1fr_1fr_1.2fr_1fr] border border-gray-600 bg-gray-50">
          <div className="px-2 py-2">
            <b>Temp:</b>{" "}
            <span className="font-semibold">{vitals?.temperature}</span> °C
          </div>
          <div className="border-l border-gray-600 px-2 py-2">
            <b>PR:</b>{" "}
            <span className="font-semibold">{vitals?.pulse_rate}</span> bpm
          </div>
          <div className="border-l border-gray-600 px-2 py-2">
            <b>RR:</b>{" "}
            <span className="font-semibold">{vitals?.respiratory_rate}</span>{" "}
            cpm
          </div>
          <div className="border-l border-gray-600 px-2 py-2">
            <b>BP:</b>{" "}
            <span className="font-semibold">{vitals?.blood_pressure}</span> mmHg
          </div>
          <div className="border-l border-gray-600 px-2 py-2">
            <b>O₂Sat:</b>{" "}
            <span className="font-semibold">{vitals?.oxygen_saturation}</span> %
          </div>
        </div>

        <TextBox
          title="History of Present Illness"
          value={external?.history_of_present_illness}
          className="min-h-[76px]"
        />
        <TextBox
          title="Physical Examination"
          value={external?.physical_examination || data?.physical_examination}
          className="min-h-[98px]"
        />

        <TextBox
          title="Investigations"
          value={data?.investigations}
          className="min-h-[54px]"
        />
        <TextBox
          title="Treatment given and/or Procedures done"
          value={external?.treatment_procedures_done || data?.treatment_given}
          className="min-h-[84px]"
        />
        <TextBox
          title="Diagnosis"
          value={external?.diagnosis || data?.provisional_diagnosis}
          className="min-h-[58px]"
        />
        <TextBox
          title="Reason(s) for referral/transfer"
          value={
            external?.referral_transfer_reason || data?.reasons_for_referral
          }
          className="min-h-[58px]"
        />

        <div className="flex min-h-[30px] items-center gap-5 border border-gray-600 bg-gray-50 px-2 py-1">
          <b>Type of referral</b>
          <CheckBox
            label="Non-urgent"
            checked={referralPriority === "Non-urgent"}
          />
          <CheckBox label="Urgent" checked={referralPriority === "Urgent"} />
          <CheckBox
            label="Emergent"
            checked={referralPriority === "Emergent"}
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-[1fr_120px_120px] items-end gap-4 font-bold">
        <div className="border-b border-dotted border-gray-800 pb-1 leading-5">
          Treating Physician's Stamp & Sign
        </div>
        <div className="border-b border-dotted border-gray-800 pb-1">
          Date: {formatDate(data?.visit_date || data?.created_at)}
        </div>
        <div className="border-b border-dotted border-gray-800 pb-1">Time:</div>
      </div>

      <div className="mt-1 grid grid-cols-[1fr_1fr_1fr] gap-6">
        <div className="flex min-h-[66px] flex-col items-center justify-end">
          <div className="text-center font-semibold">
            {data?.treating_doctor_name || practitioner?.name || ""}
          </div>
        </div>
        <div className="flex min-h-[66px] flex-col items-center justify-end">
          {stampSrc ? (
            <img
              src={stampSrc}
              alt="stamp"
              className="max-h-[54px] max-w-[115px] object-contain"
            />
          ) : (
            <div className="h-[54px]" />
          )}
        </div>
        <div className="flex min-h-[66px] flex-col items-center justify-end">
          {signatureSrc ? (
            <img
              src={signatureSrc}
              alt="signature"
              className="max-h-[50px] max-w-[165px] object-contain"
            />
          ) : (
            <div className="h-[50px]" />
          )}
        </div>
      </div>

      <div className="mt-auto">
        <ReportFooter />
      </div>
    </div>
  );
}

export default ViewExternalReferralFormReport;
